"use client";

/**
 * Search 페이지 클라이언트 컨텐츠
 *
 * 전용 검색 페이지 — 검색 입력 + 결과 그리드 + 페이지네이션
 * ShoppingContent의 검색 기능을 전용 UX로 분리
 */
import { useCallback, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Search, SearchX } from "lucide-react";

import { usePagination } from "@/shared/hooks/usePagination";
import { EmptyState } from "@/shared/ui/EmptyState";

import { ProductCard } from "@/features/shopping/components/ProductCard";
import { ProductListSkeleton } from "@/features/shopping/components/ProductListSkeleton";
import { useProductSearch } from "@/features/shopping/queries/useProducts";

import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export function SearchContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const keyword = searchParams.get("q") ?? "";
  const page = Number(searchParams.get("page") ?? "1");

  const [inputValue, setInputValue] = useState(keyword);

  useEffect(() => {
    setInputValue(keyword);
  }, [keyword]);

  const { data, isLoading, isError } = useProductSearch(keyword, page);

  const products = data?.data?.products?.list ?? [];
  const totalPages = data?.data?.products?.pagination?.total_page ?? 1;
  const totalCount = data?.data?.products?.pagination?.data_count ?? 0;

  const { navigateToPage, renderPageNumbers } = usePagination({
    page,
    totalPages,
    searchParams,
    router,
  });

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const q = inputValue.trim();
      if (q) {
        router.push(`/shopping?q=${encodeURIComponent(q)}`);
      }
    },
    [inputValue, router],
  );

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Search input */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="브랜드, 상품 검색"
            className="h-11 rounded-full pl-10 pr-4 text-base"
            autoFocus
          />
        </div>
      </form>

      {/* No keyword entered */}
      {!keyword && (
        <div className="flex flex-col items-center gap-4 text-center pt-16 pb-16">
          <p className="text-sm text-muted-foreground">검색어를 입력하고 Enter를 눌러주세요.</p>
        </div>
      )}

      {/* Loading */}
      {keyword && isLoading && <ProductListSkeleton count={8} />}

      {/* Error */}
      {keyword && isError && (
        <EmptyState
          icon={SearchX}
          title="검색 중 오류가 발생했습니다"
          description="잠시 후 다시 시도해주세요."
          blurColor="neutral-100"
          iconColor="text-neutral-300"
          action={{ label: "다시 시도", onClick: () => window.location.reload() }}
        />
      )}

      {/* Empty results */}
      {keyword && !isLoading && !isError && products.length === 0 && (
        <EmptyState
          icon={SearchX}
          title="검색 결과가 없습니다"
          description={`"${keyword}"에 대한 결과를 찾을 수 없습니다.\n다른 검색어로 시도해보세요.`}
          blurColor="neutral-50"
          iconColor="text-neutral-200"
          action={{
            label: "검색 초기화",
            onClick: () => {
              setInputValue("");
              router.push("/search");
            },
          }}
        />
      )}

      {/* Results */}
      {keyword && !isLoading && !isError && products.length > 0 && (
        <>
          <p className="text-sm text-muted-foreground mb-6">
            &ldquo;{keyword}&rdquo; 검색 결과 {totalCount}건
          </p>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} priority={i < 8} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-8">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => navigateToPage(Math.max(1, page - 1))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                {renderPageNumbers()}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => navigateToPage(Math.min(totalPages, page + 1))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
